import { apiClient } from './client';

export interface CaptureAppRelease {
  id: string;
  version: string;
  s3_key: string;
  sha256: string | null;
  size_bytes: number | null;
  notes: string | null;
  uploaded_at: string;
  uploaded_by_user_id: string | null;
  is_current: boolean;
}

export interface CaptureAppReleaseCreatePayload {
  version: string;
  size_bytes: number;
  notes?: string;
}

export interface CaptureAppReleaseCreateResponse {
  release: CaptureAppRelease;
  presigned_put_url: string;
}

export interface CaptureAppInstaller {
  /** Release version string the admin typed (e.g., "0.3.1"). */
  version: string;
  /** Long presigned GET URL — for a desktop "download" link, NOT the QR
   *  (a temp-credential presign can be ~1.5 KB → unscannable QR). */
  url: string;
  /** Short redirect URL to encode in the install QR. The API's
   *  token-bearing /download route 302s to a fresh presigned URL. */
  qr_url: string;
  /** SHA-256 hex digest the operator can spot-check against the
   *  downloaded file. Null only when a release isn't yet confirmed,
   *  which the API filters out — UI can treat as always-present. */
  sha256: string | null;
  size_bytes: number | null;
}

// --- Superadmin surface ---------------------------------------------

/** Minimum capture-app version still allowed to record (null = none).
 *  Phones below it block Start until they install the current release. */
export async function getCaptureAppMinVersion(): Promise<string | null> {
  const { data } = await apiClient.get<{ version: string | null }>(
    '/api/v1/admin/capture-app-releases/min-version',
  );
  return data.version;
}

export async function setCaptureAppMinVersion(version: string | null): Promise<string | null> {
  const { data } = await apiClient.put<{ version: string | null }>(
    '/api/v1/admin/capture-app-releases/min-version',
    { version },
  );
  return data.version;
}

/** Dotted numeric compare: negative when a < b. Non-numeric parts count as 0. */
export function compareVersions(a: string, b: string): number {
  const p = (v: string) => v.replace(/^v/i, '').split('.').map((x) => Number(x) || 0);
  const x = p(a);
  const y = p(b);
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] ?? 0) - (y[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

/** Versions that may be set as the minimum: uploaded (has a SHA-256) and
 *  no higher than the current release — the same rule the API enforces. */
export function minVersionOptions(releases: CaptureAppRelease[]): string[] {
  const current = releases.find((r) => r.is_current);
  if (!current) return [];
  const versions = releases
    .filter((r) => r.sha256 && compareVersions(r.version, current.version) <= 0)
    .map((r) => r.version);
  return [...new Set(versions)].sort((a, b) => compareVersions(b, a));
}

export async function listCaptureAppReleases(): Promise<CaptureAppRelease[]> {
  const { data } = await apiClient.get<CaptureAppRelease[]>(
    '/api/v1/admin/capture-app-releases',
  );
  return data;
}

export async function createCaptureAppRelease(
  body: CaptureAppReleaseCreatePayload,
): Promise<CaptureAppReleaseCreateResponse> {
  const { data } = await apiClient.post<CaptureAppReleaseCreateResponse>(
    '/api/v1/admin/capture-app-releases',
    body,
  );
  return data;
}

export async function confirmCaptureAppRelease(
  releaseId: string,
  promoteToCurrent: boolean,
): Promise<CaptureAppRelease> {
  const { data } = await apiClient.post<CaptureAppRelease>(
    `/api/v1/admin/capture-app-releases/${encodeURIComponent(releaseId)}/confirm`,
    { promote_to_current: promoteToCurrent },
  );
  return data;
}

export async function promoteCaptureAppRelease(
  releaseId: string,
): Promise<CaptureAppRelease> {
  const { data } = await apiClient.post<CaptureAppRelease>(
    `/api/v1/admin/capture-app-releases/${encodeURIComponent(releaseId)}/promote`,
  );
  return data;
}

export async function deleteCaptureAppRelease(
  releaseId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/admin/capture-app-releases/${encodeURIComponent(releaseId)}`,
  );
}

/** Two-step upload: POST → presigned PUT → confirm. The PUT must
 *  carry the same Content-Type the server signed for (application/
 *  vnd.android.package-archive); a plain `fetch` works because the
 *  portal isn't blocked by CORS for the S3 origin (presigned URLs
 *  short-circuit the access policy). */
export async function putApkToPresignedUrl(
  url: string,
  file: File,
): Promise<void> {
  const resp = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/vnd.android.package-archive',
    },
    body: file,
  });
  if (!resp.ok) {
    throw new Error(`S3 PUT failed (HTTP ${resp.status})`);
  }
}

// --- Tenant-admin surface -------------------------------------------

export async function getCaptureAppInstaller(
  tenantId: string,
): Promise<CaptureAppInstaller> {
  const { data } = await apiClient.get<CaptureAppInstaller>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/capture-app-installer`,
  );
  return data;
}
